import React from 'react';

import { css } from '@emotion/css';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  Tab,
  Tabs,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons.ts';
import { Loader } from '@/components/loading/Loader';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL';
import { useStyles } from '@/hooks/useStyles';
import { AlertType } from '@/models/AlertModel';
import { AlertSettingsTable } from '@/pages/patient-settings/alert-settings/AlertSettingsTable';
import { Queries } from '@/queries/Queries';
import { TabIcon } from '@/uiKit/atoms/TabIcon';

import { AlertCreationDialog } from './AlertCreationDialog';

type AlertTabsSelectorProps = {
  patientId: string;
  tab: AlertType;
  setTab: (tab: AlertType) => void;
};

const AlertTabsSelector = ({
  tab,
  setTab,
  patientId,
}: AlertTabsSelectorProps) => {
  const { t } = useTranslation();
  const alertSettingsQuery = Queries.alerts.useAlertConfigList(patientId);

  return (
    <Tabs
      id="patient-alerts-settings"
      value={tab}
      onChange={(_, value) => setTab(value)}
    >
      <Tab
        id="patient-alerts-settings-cgm"
        label={t('alertConfig.cgm')}
        iconPosition="end"
        value="cgm"
        icon={
          <TabIcon
            selected={tab === 'cgm'}
            count={
              alertSettingsQuery.data?.filter(item => item.type === 'cgm')
                .length || 0
            }
          />
        }
      />
      <Tab
        id="patient-alerts-settings-bgm"
        label={t('alertConfig.bgm')}
        iconPosition="end"
        value="bgm"
        icon={
          <TabIcon
            selected={tab === 'bgm'}
            count={
              alertSettingsQuery.data?.filter(item => item.type === 'bgm')
                .length || 0
            }
          />
        }
      />
      <Tab
        id="patient-alerts-settings-other"
        label={t('alertConfig.other')}
        iconPosition="end"
        value="other"
        icon={
          <TabIcon
            selected={tab === 'other'}
            count={
              alertSettingsQuery.data?.filter(item => item.type === 'other')
                .length || 0
            }
          />
        }
      />
    </Tabs>
  );
};

export const AlertSettings: React.FC = () => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const patientId = usePatientIdFromURL();
  const alertSettingsQuery = Queries.alerts.useAlertConfigList(patientId);
  const { data: displayMode } =
    Queries.practitioner.useDiabetesDisplayMode(patientId);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  const [tab, setTab] = React.useState<AlertType>(
    displayMode?.glycemia_indicators_display_mode || 'cgm',
  );

  return (
    <Card elevation={0} className={styles.container}>
      <CardHeader title={t('alertConfig.title')} />
      <CardContent>
        <AlertTabsSelector tab={tab} setTab={setTab} patientId={patientId} />
        {alertSettingsQuery.isSuccess ? (
          <AlertSettingsTable
            alertType={tab}
            patientId={patientId}
            alertConfigList={alertSettingsQuery.data}
          />
        ) : (
          <Loader size="L" />
        )}
      </CardContent>
      <CardActions>
        <Button
          id="create-alert-config"
          variant="contained"
          startIcon={<Icons.plus />}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          {t('alertConfig.create')}
        </Button>
      </CardActions>
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      >
        <AlertCreationDialog
          patientId={patientId}
          onClose={() => setIsCreateDialogOpen(false)}
          alertType={tab}
        />
      </Dialog>
    </Card>
  );
};

const makeStyles = () => ({
  container: css`
    flex: 1 1 0;
  `,
});
